import { useEffect, useRef } from 'react'
import type { Driver, DriveStep } from 'driver.js'
import { usePermission } from '@/providers/permissions'

const TOUR_KEY = '@Habitar:app-tour'

const STEPS: DriveStep[] = [
  {
    element: '[data-tour="agency"]',
    popover: {
      title: 'Imobiliária',
      description:
        'Clique no nome da imobiliária para gerenciar as informações, a equipe, as integrações e a assinatura, ou para trocar de imobiliária.',
      side: 'right',
      align: 'start'
    }
  },
  {
    element: '[data-tour="portfolio"]',
    popover: {
      title: 'Carteira',
      description: 'Cadastre, edite e publique os imóveis da imobiliária e acompanhe seus proprietários.',
      side: 'right',
      align: 'start'
    }
  },
  {
    element: '[data-tour="relationship"]',
    popover: {
      title: 'Relacionamento',
      description: 'Clientes, CRM e chat em um só lugar: organize visitas, negociações e conversas por etapa.',
      side: 'right',
      align: 'start'
    }
  },
  {
    element: '[data-tour="next-steps"]',
    popover: {
      title: 'Próximos passos',
      description: 'Suas missões ficam aqui: cada passo concluído dá pontos, e cada nível ganha dias grátis na assinatura.',
      side: 'right',
      align: 'end'
    }
  }
]

function tourSeen() {
  try {
    return !!localStorage.getItem(TOUR_KEY)
  } catch {
    return true
  }
}

function markTourSeen() {
  try {
    localStorage.setItem(TOUR_KEY, 'true')
  } catch {
    return
  }
}

export function AppTour() {
  const driverRef = useRef<Driver | null>(null)
  const { loading } = usePermission()

  useEffect(() => {
    if (loading || tourSeen()) return
    let cancelled = false

    const finish = () => {
      driverRef.current?.destroy()
      markTourSeen()
    }

    const timeout = setTimeout(async () => {
      const steps = STEPS.filter(step => {
        const element = typeof step.element === 'string' ? document.querySelector(step.element) : null
        return !!element && element.getClientRects().length > 0
      })
      if (steps.length === 0) return

      const { driver } = await import('driver.js')
      if (cancelled) return
      driverRef.current = driver({
        showProgress: true,
        steps,
        doneBtnText: 'Finalizar',
        progressText: 'Passo {{current}} de {{total}}',
        nextBtnText: 'Próximo',
        prevBtnText: 'Anterior',
        popoverClass: 'driverjs-theme',
        stagePadding: 5,
        smoothScroll: true,
        allowClose: true,
        overlayClickBehavior: () => undefined,
        disableActiveInteraction: true,
        allowKeyboardControl: true,
        onDestroyStarted: finish,
        onCloseClick: finish
      })
      driverRef.current.drive()
    }, 600)

    return () => {
      cancelled = true
      clearTimeout(timeout)
      driverRef.current?.destroy()
    }
  }, [loading])

  return null
}
